// AllocationDonut (TASK-496) — ported from SixEase widgets/AllocationDonut.tsx, re-grounded
// on Kielis portfolioApiService.getDrift and re-tokenised. SixEase read a single `sac_rows[]`
// with current_chf/current_pct/breach; Kielis's drift_by_asset_class gives per-sub-asset-class
// current (pct) + status + the total, so we derive current_chf = pct% × total and breach from
// status. Manual SVG donut (no chart lib), hover-dims other arcs.
import { useState } from 'react';
import { useDrift } from '../../../hooks/useDrift';
import { WidgetContainer } from '../WidgetContainer';

interface AllocRow {
  sub_asset_class: string;
  current_pct: number;
  current_chf: number;
  breach: boolean;
}

// Fixed hex palette — portable, no dependency on theme CSS-var names.
const PALETTE = ['#3b82f6', '#14b8a6', '#f59e0b', '#a855f7', '#22c55e', '#ef4444', '#64748b', '#0ea5e9', '#d946ef', '#f97316'];
const CX = 130;
const CY = 130;
const R_OUTER = 100;
const R_INNER = 62;

function chfCompact(v: number): string {
  if (!v) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${Math.round(v / 1e3)}K`;
  return `${Math.round(v)}`;
}

function arcPath(startAngle: number, endAngle: number): string {
  const x1 = CX + R_OUTER * Math.cos(startAngle);
  const y1 = CY + R_OUTER * Math.sin(startAngle);
  const x2 = CX + R_OUTER * Math.cos(endAngle);
  const y2 = CY + R_OUTER * Math.sin(endAngle);
  const xi1 = CX + R_INNER * Math.cos(endAngle);
  const yi1 = CY + R_INNER * Math.sin(endAngle);
  const xi2 = CX + R_INNER * Math.cos(startAngle);
  const yi2 = CY + R_INNER * Math.sin(startAngle);
  const large = endAngle - startAngle > Math.PI ? 1 : 0;
  return [
    `M ${x1} ${y1}`,
    `A ${R_OUTER} ${R_OUTER} 0 ${large} 1 ${x2} ${y2}`,
    `L ${xi1} ${yi1}`,
    `A ${R_INNER} ${R_INNER} 0 ${large} 0 ${xi2} ${yi2}`,
    'Z',
  ].join(' ');
}

export function AllocationDonut({ clientId }: { clientId: string }) {
  const { data, isLoading, isError, error } = useDrift(clientId);
  const [hovered, setHovered] = useState<string | null>(null);

  const d = data?.drift;
  const totalChf = d?.total_portfolio_value ?? 0;
  const allRows: AllocRow[] = Object.entries(d?.drift_by_asset_class ?? {}).map(([sub, v]) => ({
    sub_asset_class: sub,
    current_pct: v.current,
    current_chf: (v.current / 100) * totalChf,
    breach: v.status !== 'on_target',
  }));
  const riskProfile = d?.risk_profile ? d.risk_profile.charAt(0).toUpperCase() + d.risk_profile.slice(1) : '';

  return (
    <WidgetContainer source="Portfolio · Allocation">
      {isLoading && (
        <div className="space-y-3">
          <div className="h-5 w-32 animate-pulse rounded bg-accent" />
          <div className="mx-auto h-[260px] w-[260px] animate-pulse rounded-full bg-accent" />
        </div>
      )}

      {isError && (
        <div className="text-[13px]">
          <p className="text-muted-foreground">Could not load allocation — the client needs a risk profile.</p>
          <p className="mt-1 text-[11px] text-muted-foreground/70">
            {error instanceof Error ? error.message : String(error)}
          </p>
        </div>
      )}

      {data &&
        (() => {
          const rows = [...allRows].sort((a, b) => b.current_chf - a.current_chf);
          // Normalise by the actual sum so the ring always closes (pcts may not total 100).
          const totalPct = rows.reduce((s, r) => s + r.current_pct, 0) || 100;
          let angle = -Math.PI / 2;
          const arcs = rows.map((row, i) => {
            const span = (row.current_pct / totalPct) * 2 * Math.PI;
            const start = angle;
            const end = angle + span;
            angle = end;
            return { row, start, end, color: PALETTE[i % PALETTE.length] };
          });
          return (
            <div>
              <div className="mb-3 flex items-center justify-between">
                <span className="text-[15px] font-semibold text-foreground">Allocation</span>
                <span className="text-[12px] text-muted-foreground">
                  {riskProfile && `${riskProfile} risk profile`}
                </span>
              </div>

              {rows.length === 0 ? (
                <p className="text-[13px] text-muted-foreground">No allocation data for this client yet.</p>
              ) : (
                <div className="flex flex-col items-center">
                  <svg width="260" height="260" viewBox="0 0 260 260" aria-label="Allocation donut">
                    {arcs.map(({ row, start, end, color }) => (
                      <path
                        key={row.sub_asset_class}
                        d={arcPath(start, end)}
                        fill={color}
                        fillOpacity={hovered === null || hovered === row.sub_asset_class ? 1 : 0.35}
                        onMouseEnter={() => setHovered(row.sub_asset_class)}
                        onMouseLeave={() => setHovered(null)}
                        className="cursor-pointer transition-opacity"
                      />
                    ))}
                    <text x={CX} y={CY - 6} textAnchor="middle" fontSize="11" fill="hsl(var(--muted-foreground))">
                      Total CHF
                    </text>
                    <text x={CX} y={CY + 10} textAnchor="middle" fontSize="15" fontWeight="600" fill="hsl(var(--foreground))">
                      {chfCompact(totalChf)}
                    </text>
                  </svg>

                  <div className="mt-1 w-full space-y-1">
                    {arcs.map(({ row, color }) => (
                      <div
                        key={row.sub_asset_class}
                        className="flex items-center gap-2 text-[11px]"
                        onMouseEnter={() => setHovered(row.sub_asset_class)}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                        <span className="flex-1 truncate text-muted-foreground">{row.sub_asset_class}</span>
                        <span className="font-mono text-muted-foreground/70">{row.current_pct.toFixed(1)}%</span>
                        {row.breach && (
                          <span className="font-bold text-destructive" title="Drift breach" aria-label="drift breach">
                            !
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p className="mt-3 border-t border-border pt-3 text-[11px] text-muted-foreground/70">
                Source: Portfolio drift · current allocation
              </p>
            </div>
          );
        })()}
    </WidgetContainer>
  );
}
